import { useEffect, useState, useMemo } from 'react';
import { Drawer, Form, Input, Select, TreeSelect, Button, Table, Alert, Space, Row, Col, Typography, Modal, Image } from 'antd';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormItemLayout, filterFormGridLayout } from '@utils/property';
import { convertDateTimeToLocalText, buildAuthDictMaps, formatOfficeAndPost, prepareBCOSSUrl, numberProject, formatCurrency } from '@utils/utils';
import { filterTreeNodeByTitle } from '@/utils/antdTreeSelect';
import { laborFeeCostBearers, cardTypes, expertLevels, CurrencyList } from '@utils/constants';
import { getConferenceListApi } from '@services/Agenda';
import { laborFeeRequestBatchesListApi, laborFeeListApi, statisticalAmountApi } from '@services/SereviseConst';
import { conferenceOnceApi } from '@services/Conference';
import { authdictionaryApi } from '@services/Conference/sign';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import GuestTaskList from '@/components/common/GuestTaskList';
import './LaborFeeDetailDrawer.less';

const { Text } = Typography;

export default function LaborFeeDetailDrawer({ open, onClose, projectId, projectCode = 'YHY', projectName }) {
    const [filterForm] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [statisticalAmount, setStatisticalAmount] = useState({});

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filterValues, setFilterValues] = useState({});

    // 参会凭证预览
    const [previewImage, setPreviewImage] = useState(null);

    // 会议场次树数据
    const [conferenceHallTreeData, setConferenceHallTreeData] = useState([]);
    // 付款申请批次列表
    const [batchNoList, setBatchNoList] = useState([]);
    // 认证字典数据
    const [authDict, setAuthDict] = useState([]);
    // 会议信息
    const [conference, setConference] = useState(null);

    // 根据认证字典构建映射
    const dictMaps = useMemo(() => buildAuthDictMaps(authDict), [authDict]);

    // 获取币种名称
    const getCurrencyName = currencyCode => {
        const currency = CurrencyList.find(c => c.currency === currencyCode);
        return currency ? currency.name : currencyCode;
    };

    // 准备会议场次树数据
    const prepareConferenceHallTreeData = data => {
        const treeData = [];

        if (Object.keys(data) && Object.keys(data).length) {
            Object.keys(data).forEach(agendaDate => {
                const dateNode = {
                    title: agendaDate,
                    value: agendaDate,
                    selectable: false,
                    hallNum: 0,
                    children: [],
                };

                const confHalls = data[agendaDate];
                confHalls
                    && confHalls.length
                    && confHalls.forEach(confHall => {
                        const hallNode = {
                            title: `${confHall.name} [YHYC#${confHall.id}]`,
                            value: confHall.id,
                            children: [],
                        };

                        const confSubHalls = confHall.secondHallsList || [];
                        if (confSubHalls && confSubHalls.length) {
                            hallNode.selectable = false;

                            confSubHalls.forEach(subHall => {
                                dateNode.hallNum += 1;

                                hallNode.children.push({
                                    title: `${subHall.name} [YHYC#${subHall.id}]`,
                                    value: subHall.id,
                                });
                            });
                        } else {
                            dateNode.hallNum += 1;
                        }

                        dateNode.children.push(hallNode);
                    });

                treeData.push(dateNode);
            });
        }
        return treeData;
    };

    // 获取会议场次列表
    const getConferenceList = async () => {
        if (!projectId) return;
        try {
            const res = await getConferenceListApi({ conferenceId: projectId });
            if (res.status_code === 200 && res.data) {
                const agendaData = res.data;
                setConferenceHallTreeData(prepareConferenceHallTreeData(agendaData));
            }
        } catch (error) {
            console.error('获取会议场次列表失败:', error);
        }
    };

    // 请求认证字典数据
    const requestAuthDict = async () => {
        try {
            const res = await authdictionaryApi();
            if (!isEmpty(res?.options)) {
                const authDictData = res.options;
                authDictData
                    && Array.isArray(authDictData)
                    && authDictData.forEach(authType => {
                        authType.labels
                            && Array.isArray(authType.labels)
                            && authType.labels.forEach(label => {
                                label.options
                                    && Array.isArray(label.options)
                                    && label.options.forEach(l => {
                                        if (isEmpty(l.options)) {
                                            delete l.options;
                                        }
                                    });
                            });
                    });
                setAuthDict(authDictData);
            }
        } catch (error) {
            console.error('获取认证字典失败:', error);
        }
    };

    // 获取劳务费明细列表
    const getLaborFeeList = async () => {
        if (!projectId) return;

        try {
            setLoading(true);
            const params = {
                page,
                pageSize,
                projectCode,
                projectId,
                maskFlag: 1, // 默认脱敏，1-不脱敏
                ...filterValues,
            };
            const res = await laborFeeListApi(params);
            if (res && res.status_code === 200) {
                setData(res?.data?.list || []);
                setTotal(res?.data?.totalCount || 0);
            }
        } catch (error) {
            console.error('获取劳务费列表失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 获取付款申请批次列表
    const fetchBatchNoList = async () => {
        if (!projectId) return;
        try {
            const res = await laborFeeRequestBatchesListApi({
                projectCode: projectCode,
                projectId: projectId,
            });
            if (res.status_code === 200) {
                setBatchNoList(res.data || []);
            }
        } catch (error) {
            console.error('获取付款申请批次列表失败:', error);
        }
    };

    // 获取会议信息
    const getConferenceInfo = async () => {
        if (!projectId) return;
        try {
            const res = await conferenceOnceApi({ id: projectId });
            if (res.status_code === 200 && res.data) {
                setConference(res.data);
            }
        } catch (error) {
            console.error('获取会议信息失败:', error);
        }
    };

    // 劳务费和个税统计金额
    const getAmountStat = async () => {
        const res = await statisticalAmountApi({ projectCode: 'YHY', projectId });
        if (res.status_code === 200) {
            const amountStat = res?.data || null;
            setStatisticalAmount(amountStat);
        }
    };

    // 表单提交
    const onFormFinish = values => {
        setFilterValues(values);
        setPage(1);
    };

    // 重置表单
    const onReset = () => {
        filterForm.resetFields();
        setFilterValues({});
        setPage(1);
    };

    // 页码改变
    const onChangePage = (newPage, newPageSize) => {
        setPage(newPage);
        setPageSize(newPageSize);
    };

    const getTotalAmount = (laborFeeSum, individualTaxSum) => {
        if (laborFeeSum !== null && individualTaxSum !== null) {
            return (parseFloat(laborFeeSum) + parseFloat(individualTaxSum)).toFixed(2);
        }
        return '0.00';
    };

    const toggleAgendaTasks = guestAgendaInfo => {
        setData(
            data.map(item => {
                if (item.expertId === guestAgendaInfo.expertId) {
                    return { ...item, expanded: !item.expanded };
                } else {
                    return item;
                }
            }),
        );
    };

    // 获取专家证件类型
    const getIdCardType = cardType => {
        const type = parseInt(cardType, 10);
        const target = cardTypes.find(item => item.code === type);
        return target?.name || null;
    };

    // 表格列定义
    const columns = [
        {
            title: '基本信息',
            width: 200,
            dataIndex: 'memUsername',
            fixed: 'left',
            render: (_, record) => <ExpertInfoCard expert={record} />,
        },
        {
            title: '单位及职务',
            dataIndex: 'unitPost',
            width: 180,
            render: (_, row) => {
                if (row.workExpList?.length) {
                    return (
                        <>
                            {row.workExpList.map((item, index) => {
                                const officePostDisplay = formatOfficeAndPost(item, dictMaps);
                                const unitName = item.unitName || '';
                                const fullDisplay = [unitName, officePostDisplay].filter(Boolean).join('');
                                const displayIndex = index + 1;

                                return (
                                    <span key={`w_${item.id}`}>
                                        {row.workExpList.length > 1 && `${displayIndex}. `}
                                        {fullDisplay || ''}
                                    </span>
                                );
                            })}
                        </>
                    );
                } else {
                    return '—';
                }
            },
        },
        {
            title: '实际日程',
            dataIndex: 'agendasList',
            width: 400,
            render: (_, record) => {
                // 将 agendasList 映射为 agendasGuestsList 结构
                const agendasGuestsList = record.agendasList || [];
                return <GuestTaskList agendasGuestsList={agendasGuestsList} expanded={record.expanded} onToggleExpansion={toggleAgendaTasks} record={record} />;
            },
        },
        {
            title: '付款申请记录',
            dataIndex: 'laborFeePayRecordDtoList',
            width: 300,
            render: (_, record) => {
                const payRecords = record.laborFeePayRecordDtoList || [];
                if (!payRecords || payRecords.length === 0) {
                    return <Text type="secondary">暂无</Text>;
                }

                // 按卡片格式展示所有付款记录
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {payRecords.map((payRecord, index) => {
                            const costBearerLabel = laborFeeCostBearers.find(b => b.value === payRecord.costBearer)?.label || <Text type="secondary">暂无</Text>;
                            const isForeignCurrency = payRecord.currency && payRecord.currency !== 'CNY';

                            return (
                                <div key={payRecord.batchNo} className="pay-record-card">
                                    <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                                        <div>
                                            申请批次：{convertDateTimeToLocalText(payRecord.createdAt)}
                                            {payRecord.createdName && `(${payRecord.createdName})`}
                                        </div>
                                        <div>支付主体：{costBearerLabel}</div>
                                        {isForeignCurrency ? (
                                            <>
                                                <div>
                                                    外币实发额({getCurrencyName(payRecord.currency)})：
                                                    {payRecord.foreignExchangeAmount ? `${formatCurrency(payRecord.foreignExchangeAmount)}` : <Text type="secondary">暂无</Text>}
                                                    <span>({!payRecord.approvalStatus || payRecord.approvalStatus === 1 ? <Text type="warning">待提交</Text> : payRecord.payedAt ? <Text type="success">已付款</Text> : <Text type="warning">待付款</Text>})</span>
                                                </div>
                                                <div>折合人民币实发劳务费(元)：{!!payRecord.laborFeeAmount ? `${formatCurrency(payRecord.laborFeeAmount)}` : <Text type="secondary">暂无</Text>}</div>
                                            </>
                                        ) : (
                                            <div>
                                                实发劳务费(元)：{!!payRecord.laborFeeAmount ? `${formatCurrency(payRecord.laborFeeAmount)}` : <Text type="secondary">暂无</Text>}
                                                <span>({!payRecord.approvalStatus || payRecord.approvalStatus === 1 ? <Text type="warning">待提交</Text> : payRecord.payedAt ? <Text type="success">已付款</Text> : <Text type="warning">待付款</Text>})</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            title: '收款信息',
            dataIndex: 'bankName',
            width: 250,
            render: (_, record) => (
                <>
                    {(record.idcardType === 1 || !record.idcardType) && (
                        <>
                            <span>身份证号码：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY年M月D日')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                    {record.idcardType > 1 && record.bankType !== 2 && (
                        <>
                            <span>证件类型：{getIdCardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY年M月D日')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                    {record.idcardType > 1 && record.bankType === 2 && (
                        <>
                            <span>证件类型：{getIdCardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY年M月D日')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人姓名(英文)：{record.payeeUsername || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人银行预留电话：{record.payeePhone || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人境外汇款住址(英文)：{record.payeeAddress || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户行名称(英文)：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>SWIFT/BIC Code：{record.swiftBicCode || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                </>
            ),
        },
        {
            title: '参会信息',
            dataIndex: 'signTime',
            width: 200,
            render: (_, record) => (
                <>
                    <div>签到时间：{record.signTime ? convertDateTimeToLocalText(record.signTime) : <Text type="secondary">暂无</Text>}</div>
                    <div>
                        签到方式：
                        {record.checkType === 1 && '线下核销签到'}
                        {record.checkType === 2 && '线上自助签到'}
                        {record.checkType === 3 && <Text type="danger">后台代签</Text>}
                        {!record.checkType && <Text type="secondary">暂无</Text>}
                    </div>
                    <div>
                        参会凭证：
                        {record.joinCert ? (
                            <Button type="link" size="small" style={{ paddingLeft: 0, paddingRight: 0 }} onClick={() => setPreviewImage(record.joinCert)}>
                                查看
                            </Button>
                        ) : (
                            <Text type="secondary">暂无</Text>
                        )}
                    </div>
                </>
            ),
        },
    ];

    useEffect(() => {
        if (open && projectId) {
            requestAuthDict();
            getConferenceList();
            fetchBatchNoList();
            getConferenceInfo();
            getAmountStat();
        }
    }, [open, projectId]);

    useEffect(() => {
        if (open && projectId) {
            getLaborFeeList();
        }
    }, [open, projectId, page, pageSize, filterValues]);

    // 关闭时重置状态
    const handleClose = () => {
        filterForm.resetFields();
        setFilterValues({});
        setPage(1);
        setPageSize(10);
        setData([]);
        onClose();
    };

    return (
        <Drawer title={<span style={{ display: 'block', textAlign: 'center' }}>{projectName} - 劳务费明细</span>} placement="bottom" size="100%" open={open} onClose={handleClose} className="labor-fee-detail-drawer" destroyOnHidden closable={{ placement: 'end' }}>
            <div className="drawer-content">
                {/* 筛选表单 */}
                <div className="filter-form-box">
                    <Form {...filterFormItemLayout} form={filterForm} onFinish={onFormFinish} autoComplete="off">
                        <Row>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="memUsername" label="姓名">
                                    <Input placeholder="请输入专家姓名" allowClear />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="memMobile" label="手机号">
                                    <Input placeholder="请输入手机号" allowClear />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="专家级别" name="expertLevels">
                                    <Select mode="multiple" maxTagCount="responsive" options={expertLevels} fieldNames={{ label: 'label', value: 'level' }} placeholder="请选择专家级别" allowClear />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="conferenceHallId" label="任务所属会议">
                                    <TreeSelect showSearch={{ filterTreeNode: filterTreeNodeByTitle }} styles={{ popup: { root: { minWidth: 500, overflow: 'auto' } } }} allowClear treeDefaultExpandAll placement="bottomRight" treeData={conferenceHallTreeData} placeholder="请选择会议" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="batchStatus" label="付款申请">
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={1}>已申请</Select.Option>
                                        <Select.Option value={0}>未申请</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="batchNo" label="付款申请时间">
                                    <Select placeholder="请选择" allowClear>
                                        {batchNoList.map(item => (
                                            <Select.Option key={item.batchNo} value={item.batchNo}>
                                                {convertDateTimeToLocalText(item.createdAt)}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="laborPayStatus" label="付款状态">
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>待付款</Select.Option>
                                        <Select.Option value={1}>已付款</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="costBearer" label="支付主体">
                                    <Select placeholder="请选择" allowClear options={laborFeeCostBearers} fieldNames={{ label: 'label', value: 'value' }} />
                                </Form.Item>
                            </Col>
                            <Col className="filter-form-buttons" {...filterFormGridLayout}>
                                <Space>
                                    <Button type="default" onClick={onReset}>
                                        重置
                                    </Button>
                                    <Button type="primary" icon={<i className="bi bi-search"></i>} htmlType="submit">
                                        检索
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Form>
                </div>

                {/* 统计信息 */}
                <div className="statistics-alert">
                    <Alert
                        type="info"
                        style={{ marginBottom: 16, fontSize: 12 }}
                        title={
                            <>
                                <span style={{ fontSize: 12, fontWeight: 600 }}>
                                    项目信息：
                                    <span style={{ fontSize: 12, fontWeight: 400 }}>
                                        {conference?.name || '-'}， 氚云项目编号：{conference?.h3yunNo || '-'}， 会议项目编号：{numberProject('YHY', conference?.id) || '-'}
                                    </span>
                                </span>
                                <br />
                                <span style={{ fontSize: 12, fontWeight: 600 }}>
                                    本项目累计付款申请：
                                    <span style={{ fontSize: 12, fontWeight: 400 }}>
                                        专家总数：{statisticalAmount?.expertSum}人，需要劳务费人数：{statisticalAmount?.realTotal}人，付款申请人数：{statisticalAmount?.expertAppliedSum}人，已付款人数：{statisticalAmount?.expertPaySum}人，实发劳务费合计金额（元）：{formatCurrency(statisticalAmount?.LaborFeeSum)}
                                        ，个人所得税合计金额（元）：{formatCurrency(statisticalAmount?.IndividualTaxSum)}，含税劳务费合计金额（元）：{formatCurrency(getTotalAmount(statisticalAmount?.LaborFeeSum, statisticalAmount?.IndividualTaxSum))}
                                    </span>
                                </span>
                                <br />
                                <span style={{ fontSize: 12, fontWeight: 600 }}>
                                    劳务需求备注：<span style={{ color: '#faad14' }}> {conference?.laborFeeDemandRemarks || '未填写'}</span>
                                </span>
                            </>
                        }
                    />
                </div>

                {/* 表格 */}
                <div className="table-box">
                    <Table
                        rowKey="expertId"
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        scroll={{ x: 1500 }}
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: page,
                            pageSize: pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            showQuickJumper: true,
                            showSizeChanger: true,
                            onChange: onChangePage,
                        }}
                    />
                </div>
            </div>

            {/* 参会凭证图片预览 Modal */}
            <Modal title="图片预览" open={!!previewImage} onCancel={() => setPreviewImage(null)} footer={null} centered width="auto" styles={{ body: { padding: 0, textAlign: 'center', background: '#f0f0f0' } }} destroyOnHidden>
                {previewImage && <Image src={prepareBCOSSUrl(previewImage)} style={{ maxWidth: '90vw', maxHeight: '90vh' }} preview={false} alt="图片预览" />}
            </Modal>
        </Drawer>
    );
}
